import React, { useState } from 'react';

const STATE_BASE_RATES = {
  AL:285, AK:480, AZ:240, AR:390, CA:310, CO:230, CT:330, DE:300, FL:295, GA:305,
  HI:255, ID:260, IL:275, IN:265, IA:250, KS:295, KY:300, LA:275, ME:355, MD:290,
  MA:260, MI:265, MN:270, MS:295, MO:280, MT:345, NE:320, NV:225, NH:195, NJ:295,
  NM:270, NY:380, NC:300, ND:370, OH:255, OK:290, OR:250, PA:280, RI:295, SC:285,
  SD:345, TN:305, TX:290, UT:240, VT:780, VA:285, WA:260, WV:385, WI:270, WY:420,
};

// Adult age factors — base rate is age 40
const AGE_FACTORS = {
  // Young adults 18–26
  18:0.600, 19:0.620, 20:0.640, 21:0.660, 22:0.665, 23:0.670, 24:0.675, 25:0.690, 26:0.710,
  // Adults 27–64
  27:0.735, 28:0.770, 29:0.800, 30:0.820, 31:0.845, 32:0.870, 33:0.888,
  34:0.905, 35:0.915, 36:0.925, 37:0.935, 38:0.945, 39:0.955, 40:1.000, 41:1.010,
  42:1.020, 43:1.030, 44:1.040, 45:1.060, 46:1.090, 47:1.120, 48:1.150, 49:1.180,
  50:1.220, 51:1.255, 52:1.295, 53:1.340, 54:1.390, 55:1.460, 56:1.540, 57:1.630,
  58:1.720, 59:1.820, 60:1.920, 61:2.020, 62:2.120, 63:2.220, 64:2.320,
  // Seniors 65–85 (private non-Medicare plans)
  65:2.50, 66:2.65, 67:2.80, 68:2.95, 69:3.10,
  70:3.30, 71:3.50, 72:3.70, 73:3.90, 74:4.10,
  75:4.35, 76:4.60, 77:4.85, 78:5.10, 79:5.40,
  80:5.70, 81:6.00, 82:6.30, 83:6.65, 84:7.00, 85:7.35,
};

// Child-only plan factors for ages 0–17 (relative to age-40 base rate)
const CHILD_AGE_FACTORS = {
  0:0.38, 1:0.38, 2:0.38, 3:0.38, 4:0.38,
  5:0.34, 6:0.34, 7:0.34, 8:0.34, 9:0.34,
  10:0.37, 11:0.37, 12:0.37,
  13:0.44, 14:0.44, 15:0.44,
  16:0.54, 17:0.54,
};

function getSupplementCost(age) {
  if (age < 18) return 0;
  if (age <= 39) return 50;
  if (age <= 49) return 75;
  if (age <= 54) return 100;
  return 125;
}

const STATE_NAMES = {
  AL:'Alabama', AK:'Alaska', AZ:'Arizona', AR:'Arkansas', CA:'California', CO:'Colorado',
  CT:'Connecticut', DE:'Delaware', FL:'Florida', GA:'Georgia', HI:'Hawaii', ID:'Idaho',
  IL:'Illinois', IN:'Indiana', IA:'Iowa', KS:'Kansas', KY:'Kentucky', LA:'Louisiana',
  ME:'Maine', MD:'Maryland', MA:'Massachusetts', MI:'Michigan', MN:'Minnesota', MS:'Mississippi',
  MO:'Missouri', MT:'Montana', NE:'Nebraska', NV:'Nevada', NH:'New Hampshire', NJ:'New Jersey',
  NM:'New Mexico', NY:'New York', NC:'North Carolina', ND:'North Dakota', OH:'Ohio',
  OK:'Oklahoma', OR:'Oregon', PA:'Pennsylvania', RI:'Rhode Island', SC:'South Carolina',
  SD:'South Dakota', TN:'Tennessee', TX:'Texas', UT:'Utah', VT:'Vermont', VA:'Virginia',
  WA:'Washington', WV:'West Virginia', WI:'Wisconsin', WY:'Wyoming',
};

const DEFAULT_LABELS = ['Client', 'Spouse', 'Child 1', 'Child 2', 'Child 3', 'Child 4', 'Child 5'];

function isChild(label) {
  return label.startsWith('Child');
}

function ageRange(label) {
  return isChild(label) ? { min: 0, max: 26 } : { min: 18, max: 85 };
}

function calcTotal(age, label, stateCode) {
  const a = parseInt(age);
  const { min, max } = ageRange(label);
  if (isNaN(a) || a < min || a > max) return null;
  const base = STATE_BASE_RATES[stateCode] || 290;
  const factor = a < 18 ? (CHILD_AGE_FACTORS[a] ?? 0.38) : (AGE_FACTORS[a] ?? 1.0);
  const premium = Math.round(base * factor * 100) / 100;
  const supplement = getSupplementCost(a);
  return Math.round((premium + supplement) * 100) / 100;
}

function generatePricingTable(stateCode) {
  const groups = [
    { label: '0–4',   age: 2  }, { label: '5–9',   age: 7  },
    { label: '10–12', age: 11 }, { label: '13–15', age: 14 },
    { label: '16–17', age: 17 }, { label: '18–20', age: 19 },
    { label: '21–24', age: 22 }, { label: '25–29', age: 27 },
    { label: '30–34', age: 32 }, { label: '35–39', age: 37 },
    { label: '40–41', age: 40 },
    ...Array.from({ length: 23 }, (_, i) => ({ label: String(42 + i), age: 42 + i })),
    { label: '65–69', age: 67 }, { label: '70–74', age: 72 },
    { label: '75–79', age: 77 }, { label: '80–85', age: 82 },
  ];
  return groups.map(g => {
    const base = STATE_BASE_RATES[stateCode] || 290;
    const factor = g.age < 18 ? (CHILD_AGE_FACTORS[g.age] ?? 0.38) : (AGE_FACTORS[g.age] ?? 1.0);
    const premium = Math.round(base * factor * 100) / 100;
    const supplement = getSupplementCost(g.age);
    return { label: g.label, premium, supplement };
  });
}

function fmt(n) { return '$' + n.toFixed(2); }

let _uid = 1;
function makePerson(label) { return { id: _uid++, label, age: '' }; }

export default function Estimator() {
  const [stateCode, setStateCode] = useState('');
  const [people, setPeople] = useState([makePerson('Client')]);
  const [quotes, setQuotes] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [tableState, setTableState] = useState('FL');

  const addPerson = () => {
    const used = new Set(people.map(p => p.label));
    const nextLabel = DEFAULT_LABELS.find(l => !used.has(l)) || `Person ${people.length + 1}`;
    setPeople(prev => [...prev, makePerson(nextLabel)]);
    setQuotes(null);
  };

  const removePerson = (id) => {
    if (people.length <= 1) return;
    setPeople(prev => prev.filter(p => p.id !== id));
    setQuotes(null);
  };

  const updateAge = (id, val) => {
    setPeople(prev => prev.map(p => p.id === id ? { ...p, age: val } : p));
    setQuotes(null);
  };

  const generateQuote = () => {
    if (!stateCode) return alert('Please select a state.');
    setQuotes(people.map(p => ({ ...p, total: calcTotal(p.age, p.label, stateCode) })));
    setTableState(stateCode);
  };

  const validQuotes = quotes ? quotes.filter(q => q.total !== null) : [];
  const totalMonthly = validQuotes.reduce((sum, q) => sum + q.total, 0);
  const pricingRows = generatePricingTable(tableState);

  const card = { background: 'var(--bg2)', borderRadius: 16, border: '0.5px solid rgba(83,74,183,0.3)', overflow: 'hidden', boxShadow: '0 0 30px rgba(83,74,183,0.08)' };
  const hdr  = { padding: '14px 20px', borderBottom: '0.5px solid var(--border)', background: 'linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))', display: 'flex', alignItems: 'center', gap: 10 };
  const dot  = (c = '#534AB7') => ({ width: 8, height: 8, borderRadius: '50%', background: c, boxShadow: `0 0 8px ${c}` });
  const lbl  = { fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' };
  const inp  = { width: '100%', padding: '9px 12px', borderRadius: 9, border: '0.5px solid var(--border2)', background: 'var(--bg3)', fontSize: 13, color: 'var(--text)', boxSizing: 'border-box' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>

      {/* ── People ── */}
      <div style={card}>
        <div style={hdr}>
          <div style={dot()} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>People</span>
          <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 4 }}>{people.length} {people.length === 1 ? 'person' : 'people'}</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, padding: '3px 10px', borderRadius: 20, background: 'rgba(29,158,117,0.1)', border: '0.5px solid rgba(29,158,117,0.3)', color: '#1D9E75', fontWeight: 600 }}>Private / Non-ACA</span>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* State */}
          <div>
            <div style={lbl}>State (applies to all)</div>
            <select value={stateCode} onChange={e => { setStateCode(e.target.value); setQuotes(null); }}
              style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Select state...</option>
              {Object.entries(STATE_NAMES).sort((a, b) => a[1].localeCompare(b[1])).map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </div>

          {/* Person rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {people.map(p => {
              const range = ageRange(p.label);
              return (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--bg3)', border: '0.5px solid var(--border)' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', minWidth: 68, flexShrink: 0 }}>{p.label}</span>
                  <input
                    value={p.age}
                    onChange={e => updateAge(p.id, e.target.value)}
                    type="number"
                    min={range.min}
                    max={range.max}
                    placeholder={`Age (${range.min}–${range.max})`}
                    style={{ ...inp, background: 'var(--bg2)', flex: 1 }}
                  />
                  <button onClick={() => removePerson(p.id)} disabled={people.length === 1}
                    title={people.length === 1 ? 'At least one person required' : 'Remove'}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '0.5px solid var(--border2)', background: 'transparent', cursor: people.length === 1 ? 'default' : 'pointer', color: 'var(--text3)', opacity: people.length === 1 ? 0.3 : 1, fontSize: 14, flexShrink: 0 }}>
                    <i className="ti ti-x" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Add Person + Generate */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={addPerson}
              style={{ padding: '10px 16px', borderRadius: 10, border: '0.5px solid rgba(83,74,183,0.4)', background: 'rgba(83,74,183,0.08)', color: '#534AB7', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <i className="ti ti-user-plus" aria-hidden="true" />
              Add Person
            </button>
            <button onClick={generateQuote}
              style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#534AB7,#1D9E75)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(83,74,183,0.35)' }}>
              Generate Quote
            </button>
          </div>
        </div>
      </div>

      {/* ── Quote Results ── */}
      <div style={card}>
        <div style={hdr}>
          <div style={dot()} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Quote Results</span>
          {quotes && stateCode && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>
              {validQuotes.length} of {quotes.length} quoted · {STATE_NAMES[stateCode]}
            </span>
          )}
        </div>
        <div style={{ padding: 20 }}>
          {!quotes ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)', fontSize: 13, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <i className="ti ti-calculator" style={{ fontSize: 36, opacity: 0.3 }} aria-hidden="true" />
              Add people and generate a quote to see estimates
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

              {/* Per-person rows */}
              {quotes.map(q => {
                const range = ageRange(q.label);
                return q.total !== null ? (
                  <div key={q.id} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderRadius: 10, background: 'var(--bg3)', border: '0.5px solid var(--border)' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', minWidth: 90 }}>{q.label}</span>
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>Age {q.age}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 18, fontWeight: 800, color: '#534AB7' }}>
                      {fmt(q.total)}<span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)', marginLeft: 4 }}>/mo</span>
                    </span>
                  </div>
                ) : (
                  <div key={q.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: 10, border: '0.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text3)', fontSize: 13 }}>
                    <i className="ti ti-alert-circle" style={{ fontSize: 15 }} aria-hidden="true" />
                    <span><strong style={{ color: 'var(--text2)' }}>{q.label}</strong> — enter a valid age ({range.min}–{range.max})</span>
                  </div>
                );
              })}

              {/* Combined total */}
              {validQuotes.length > 0 && (
                <div style={{ marginTop: 4, padding: '18px 20px', borderRadius: 12, background: 'linear-gradient(135deg,rgba(83,74,183,0.1),rgba(29,158,117,0.08))', border: '0.5px solid rgba(83,74,183,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Combined Monthly Estimate</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>{STATE_NAMES[stateCode]} · {validQuotes.length} {validQuotes.length === 1 ? 'person' : 'people'}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 34, fontWeight: 900, color: '#D85A30', lineHeight: 1 }}>{fmt(totalMonthly)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>per month · {fmt(totalMonthly * 12)}/yr</div>
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(29,158,117,0.06)', border: '0.5px solid rgba(29,158,117,0.2)', fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
                <strong style={{ color: '#1D9E75' }}>Note:</strong> Estimates are for private medically-underwritten PPO plans (healthy non-smoker) plus an optional supplement add-on. Ages 65+ reflect private non-Medicare plans. Actual rates depend on health history and carrier. Not an ACA product.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Full Pricing Reference ── */}
      <div style={card}>
        <div style={{ ...hdr, cursor: 'pointer', justifyContent: 'space-between' }} onClick={() => setShowTable(s => !s)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={dot('#1D9E75')} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Full Pricing Reference</span>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>— {STATE_NAMES[tableState]}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select value={tableState} onClick={e => e.stopPropagation()} onChange={e => setTableState(e.target.value)}
              style={{ padding: '5px 10px', borderRadius: 8, border: '0.5px solid var(--border2)', background: 'var(--bg3)', fontSize: 11, color: 'var(--text)', cursor: 'pointer' }}>
              {Object.entries(STATE_NAMES).sort((a, b) => a[1].localeCompare(b[1])).map(([code, name]) => (
                <option key={code} value={code}>{code} – {name}</option>
              ))}
            </select>
            <i className={'ti ' + (showTable ? 'ti-chevron-up' : 'ti-chevron-down')} style={{ fontSize: 14, color: 'var(--text2)' }} aria-hidden="true" />
          </div>
        </div>

        {showTable ? (
          <div style={{ overflow: 'auto', maxHeight: 500 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg3)', position: 'sticky', top: 0 }}>
                  {['Age', 'Base Premium', 'Supplement Add-on', 'Total'].map(h => (
                    <th key={h} style={{ padding: '10px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '0.5px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pricingRows.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{row.label}</td>
                    <td style={{ padding: '10px 20px', fontSize: 13, color: '#534AB7', fontWeight: 500 }}>{fmt(row.premium)}</td>
                    <td style={{ padding: '10px 20px', fontSize: 13, color: '#1D9E75', fontWeight: 500 }}>{row.supplement > 0 ? fmt(row.supplement) : '—'}</td>
                    <td style={{ padding: '10px 20px', fontSize: 13, color: '#D85A30', fontWeight: 600 }}>{fmt(row.premium + row.supplement)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '14px 20px', fontSize: 12, color: 'var(--text3)' }}>Click to expand the full pricing table for any state.</div>
        )}
      </div>

    </div>
  );
}
